import * as React from "react";
import { useState } from "react";
import { Breadcrumbs } from "@arctheme-components/sections/Breadcrumbs/Breadcrumbs";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import { CollectionSidebar } from "@project-stories/ecommerce/Collection/CollectionSidebar";
import { CollectionToolbar } from "@project-stories/ecommerce/Collection/CollectionToolbar";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { LoadingSpinner } from "@arctheme-components/elements/LoadingSpinner/LoadingSpinner";
import {ReactifySearchProvider, Results, Search, Sensors, SortBy, useSearch} from "@usereactify/search";
import SearchResultsBar from "./SearchResultsBar";
import {LoadMore} from "@project-stories/client/LoadMore/LoadMore";

export const SearchResults = ({ settings }) => {
  const [query, setQuery] = useState("");
  const [products, setProducts]: any = useState([]);
  const [filters, setFilters] = useState([]);
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const [isSidebarVisible, setSidebarVisible] = useState(false);

  const useProvider = true;
  const toggleSidebar = () => {
    setSidebarVisible(!isSidebarVisible);
  };

  const {
    columns_mobile,
    columns_desktop,
    column_gap_mobile,
    column_gap_desktop,
    row_gap_mobile,
    row_gap_desktop,
    id,
    heading_type,
  } = settings.section;

  const CustomTag = heading_type as keyof JSX.IntrinsicElements;
  const showBreadcrumbs = false;

  React.useEffect(() => {
    const handleFiltersResults = (event) => {
      if (event.detail.type === "search") {
        if (event.detail.filters) {
          setFilters(event.detail.filters);
        }
      }
    };

    const handleProductsResults = (event) => {
      if (event.detail.type === "search") {
        const { products, query } = event.detail;
        setQuery(query);
        setProducts(products);
        window.dispatchEvent(
          new CustomEvent("hide-spinner", {
            detail: {
              id: "spinner-collection",
            },
          }),
        );
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setSidebarVisible(false);
      }
    };
    window.dispatchEvent(new CustomEvent("collection-search-loaded"));

    window.addEventListener("product-list", handleProductsResults);
    window.addEventListener("filter-list", handleFiltersResults);
    window.addEventListener("keydown", handleEscapeKey);

    // Cleanup the event listeners on component unmount
    return () => {
      // window.removeEventListener("product-list", handleProductsResults);
      // window.removeEventListener("filter-list", handleFiltersResults);
      // window.removeEventListener("keydown", handleEscapeKey);
    };
  }, [products, query]);

  const gridColumns = isMobile ? 2 : 4;

  return (
      <ReactifySearchProvider
          mode="search"
          shopifyPermanentDomain={`${settings?.shop?.domain}`}
          includeFields={[
            "*"
          ]}
      >
        <Sensors />
        <div className={`flex flex-col gap-[20px] pb-[32px] pt-[40px]`}>
          <SearchResultsBar settings={settings} isMobile={isMobile} query={query} products={products} />
          <div>
            <Results
                listStyle={{}}
                listClassName={`grid grid-cols-${gridColumns} gap-[16px] lg:gap-[24px] pb-[32px] pt-[40px] `}
                renderPaginationLoadMore={(props) => <LoadMore {...props} />}
                renderResultCardProduct={(productProps) =>
                    <ProductTile {...productProps} settings={settings} enableWishlist={true} />
                }
            />
          </div>
        </div>

        {!useProvider && <div
            x-init={`initialise(null, false, ${settings.search_size}, ${settings.pagination_type})`}
        >
          <CollectionSidebar
              isVisible={isSidebarVisible}
              filters={settings.collection_filters}
              metaobject_filters={settings.filters}
              toggleSidebar={toggleSidebar}
          />

          <div className="search-results__toolbar flex justify-between mb-6 lg:mb-12 items-center">
            {!isMobile && showBreadcrumbs && (
                <Breadcrumbs
                    templateName={settings.template.name}
                    shop={settings.shop}
                    collection={null}
                    product={null}
                    routes={settings.routes}
                    blog={null}
                    article={null}
                    page={settings.page}
                    request={settings.request}
                    pageTitle={query}
                />
            )}
            <CollectionToolbar toggleSidebar={toggleSidebar}/>
          </div>

          <div
              id="product-grid"
              className={`search product-grid grid grid-cols-${columns_mobile}
              lg:grid-cols-${columns_desktop} gap-x-[${column_gap_mobile}px]
              lg:gap-x-[${column_gap_desktop}px] gap-y-[${row_gap_mobile}px]
              lg:gap-y-[${row_gap_desktop}px]
              auto-cols-max`}
              data-id={id}
          >
            <LoadingSpinner id="spinner-collection"/>

            {products.map((product, index) => (
                <React.Fragment key={index}>
                  <div className={"search-tile"}>
                    <ProductTile product={product} siblings={undefined}/>
                  </div>
                </React.Fragment>
            ))}
          </div>
          {products.length <= 0 && query.length > 0 && (
              <div
                  className="collection collection--empty page-width"
                  id="product-grid"
                  data-id="{{ section.id }}"
              >
                <div className="loading-overlay gradient"></div>
                <div className="title-wrapper center">
                  <p className="title title--primary h2">
                    Unfortunately, nothing matches your search. Please try another
                    search term, or browse by category below.
                  </p>
                </div>
              </div>
          )}
        </div>}
      </ReactifySearchProvider>
  );
};

export default SearchResults;
